/**
 * Monster catalog: meals are bosses. Scouting a boss = reading the label and
 * doing the servings math, which IS the carb-counting practice.
 */

export interface FoodMonster {
  id: string;
  name: string;
  taunt: string;
  /** 1 = trash mob ... 3 = raid boss */
  tier: 1 | 2 | 3;
  /** servings defeated (eaten) during the encounter */
  servings: number;
  carbsPerServing: number;
  /** fiber grams subtracted for net carbs (game simplification) */
  fiberGrams: number;
  /** 0 = fast carbs, 2 = double-cheese armor (slows absorption) */
  fatLevel: 0 | 1 | 2;
  /** the label card shown during scouting — reading this IS the skill */
  labelCard: string;
}

export const FOODS: FoodMonster[] = [
  {
    id: 'pizza',
    name: 'Pizza the Destroyer',
    taunt: '"You cannot defeat me! I have EXTRA CHEESE!"',
    tier: 2,
    servings: 2.5,
    carbsPerServing: 34,
    fiberGrams: 4,
    fatLevel: 2,
    labelCard:
      'LABEL CARD\nCheese Pizza\nCarbs: 34 g per slice (1 slice = 1 serving)\nFiber: 4 g per slice\nYou are battling 2.5 slices.',
  },
  {
    id: 'juice',
    name: 'Juice Gremlin',
    taunt: '"I am small but SUGARY. Fear me!"',
    tier: 1,
    servings: 1.5,
    carbsPerServing: 26,
    fiberGrams: 0,
    fatLevel: 0,
    labelCard:
      'LABEL CARD\nFruit Juice\nCarbs: 26 g per glass (1 glass = 1 serving)\nYou are battling 1.5 glasses.',
  },
  {
    id: 'pasta',
    name: 'Pasta Hydra',
    taunt: '"Cut one noodle, two more grow back!"',
    tier: 2,
    servings: 1.5,
    carbsPerServing: 40,
    fiberGrams: 6,
    fatLevel: 1,
    labelCard:
      'LABEL CARD\nWhole-wheat Pasta\nCarbs: 40 g per cup\nFiber: 4 g per cup (total for 1.5 cups: 6 g)\nYou are battling 1.5 cups.',
  },
  {
    id: 'cookies',
    name: 'Cookie Golem',
    taunt: '"I crumble under pressure. Do you?"',
    tier: 1,
    servings: 4,
    carbsPerServing: 16,
    fiberGrams: 1,
    fatLevel: 2,
    labelCard:
      'LABEL CARD\nChoc-chip Cookies\nCarbs: 16 g per cookie\nFiber: 0.25 g per cookie (total for 4: 1 g)\nYou are battling 4 cookies.',
  },
  {
    id: 'beansrice',
    name: 'Beans-and-Rice Behemoth',
    taunt: '"I am steady fuel for steady heroes..."',
    tier: 1,
    servings: 2,
    carbsPerServing: 30,
    fiberGrams: 8,
    fatLevel: 0,
    labelCard:
      'LABEL CARD\nRice & Beans Bowl\nCarbs: 30 g per cup\nFiber: 4 g per cup (total for 2 cups: 8 g)\nYou are battling 2 cups.',
  },
  {
    id: 'cake',
    name: 'Birthday Cake Leviathan',
    taunt: '"Nobody beats me at a PARTY."',
    tier: 3,
    servings: 2,
    carbsPerServing: 45,
    fiberGrams: 2,
    fatLevel: 2,
    labelCard:
      'LABEL CARD\nBirthday Cake\nCarbs: 45 g per slice\nFiber: 1 g per slice (total for 2 slices: 2 g)\nYou are battling 2 slices.',
  },
];

/** Net carbs the boss actually delivers (the world uses the TRUE number). */
export function trueCarbs(f: FoodMonster): number {
  return Math.round(f.servings * f.carbsPerServing - f.fiberGrams);
}

/** Deterministic scouting options: the true value plus two distractors. */
export function scoutingOptions(f: FoodMonster): number[] {
  const correct = trueCarbs(f);
  const options = new Set([correct, correct + 15, Math.max(0, correct - 25)]);
  // Set may collapse distractors; top up with another distractor if needed.
  if (options.size < 3) options.add(correct + 30);
  return [...options];
}
